"use client"

import { useState, useRef, useEffect } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Search, X } from "lucide-react"
import { cn, DISCIPLINES } from "@/lib/utils"
import { useLocale } from "@/components/LocaleProvider"

const CONTINENTS = [
  "North America",
  "South America",
  "Europe",
  "Africa",
  "Asia",
  "Oceania",
]

// US state shortcuts surface inside the WHERE dropdown. Each routes to its
// featured-collection landing page rather than setting a continent filter
// — that page already covers regional intent + has its own SEO surface.
const STATE_LINKS: Array<{ label: string; href: string }> = [
  { label: "New York", href: "/featured/new-york-residencies" },
  { label: "California", href: "/featured/california-residencies" },
  { label: "Vermont", href: "/featured/vermont-residencies" },
  { label: "Maine", href: "/featured/maine-residencies" },
  { label: "Texas", href: "/featured/texas-residencies" },
  { label: "Massachusetts", href: "/featured/massachusetts-residencies" },
]

const COST_TYPES = [
  { value: "free", label: "Free" },
  { value: "stipend", label: "Paid Stipend" },
  { value: "fee", label: "Program Fee" },
  { value: "self-funded", label: "Self-Funded" },
]

type Field = "name" | "what" | "where" | "funding" | null

type Mode = "home" | "directory" | "openCalls"

type Props = {
  /** "home" submits to /, "directory" submits to /directory, "openCalls"
   *  submits to /open-calls. Defaults to "home". */
  mode?: Mode
  /** Tiny overline above the bar making it explicit what's being searched.
   *  e.g. "Open calls · search opportunities by deadline" on the homepage,
   *  or "Directory · search residency organizations" on /directory. */
  label?: string
}

export function OpenCallSearchBar({ mode = "home", label }: Props = {}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const barRef = useRef<HTMLDivElement>(null)
  const { t } = useLocale()

  const [activeField, setActiveField] = useState<Field>(null)
  const [name, setName] = useState(searchParams.get("search") || "")
  const [discipline, setDiscipline] = useState(searchParams.get("discipline") || "")
  const [continent, setContinent] = useState(searchParams.get("continent") || "")
  const [costType, setCostType] = useState(searchParams.get("costType") || "")

  useEffect(() => {
    setName(searchParams.get("search") || "")
    setDiscipline(searchParams.get("discipline") || "")
    setContinent(searchParams.get("continent") || "")
    setCostType(searchParams.get("costType") || "")
  }, [searchParams])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setActiveField(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const hasFilters = name || discipline || continent || costType

  const basePath = mode === "directory" ? "/directory" : mode === "openCalls" ? "/open-calls" : "/"

  function buildUrl(overrides: { name?: string; discipline?: string; continent?: string; costType?: string } = {}) {
    const n = overrides.name ?? name
    const d = overrides.discipline ?? discipline
    const c = overrides.continent ?? continent
    const ct = overrides.costType ?? costType
    // On the directory we preserve all pre-existing params (sort, view,
    // page) — only the four segmented fields are overridden. On the
    // homepage there's nothing to preserve.
    const params = mode === "directory"
      ? new URLSearchParams(searchParams.toString())
      : new URLSearchParams()
    if (n) params.set("search", n); else params.delete("search")
    if (d) params.set("discipline", d); else params.delete("discipline")
    if (c) params.set("continent", c); else params.delete("continent")
    if (ct) params.set("costType", ct); else params.delete("costType")
    if (mode === "directory") params.delete("page")
    const query = params.toString()
    return query ? `${basePath}?${query}` : basePath
  }

  function handleSearch() {
    router.push(buildUrl())
    setActiveField(null)
  }

  function handleClear() {
    setName("")
    setDiscipline("")
    setContinent("")
    setCostType("")
    router.push(basePath)
    setActiveField(null)
  }

  function commitName(value: string) {
    const next = value.trim()
    setName(next)
    router.push(buildUrl({ name: next }))
    setActiveField(null)
  }

  function selectDiscipline(value: string) {
    const next = discipline === value ? "" : value
    setDiscipline(next)
    router.push(buildUrl({ discipline: next }))
    setActiveField(null)
  }

  function selectContinent(value: string) {
    const next = continent === value ? "" : value
    setContinent(next)
    router.push(buildUrl({ continent: next }))
    setActiveField(null)
  }

  function selectCostType(value: string) {
    const next = costType === value ? "" : value
    setCostType(next)
    router.push(buildUrl({ costType: next }))
    setActiveField(null)
  }

  function selectStateLink(href: string) {
    router.push(href)
    setActiveField(null)
  }

  const costLabel = COST_TYPES.find((c) => c.value === costType)?.label

  return (
    <div className="relative" ref={barRef}>
      {label && (
        <p className="text-[10px] uppercase tracking-widest text-[var(--muted-foreground)] mb-1.5 font-semibold">
          {label}
        </p>
      )}
      {/* Desktop: segmented search bar — NAME / WHAT / WHERE / FUNDING.
          Name is an inline text input, the other three open dropdowns. */}
      <div className="hidden md:flex items-center border border-[var(--border)] rounded-lg hover:border-[var(--muted-foreground)]/30 transition-colors bg-white">
        {/* Name — inline editable */}
        <div
          className={cn(
            "flex-1 min-w-0 px-4 py-2 rounded-l-lg transition-colors",
            activeField === "name" ? "bg-[var(--secondary)]" : "hover:bg-[var(--secondary)]/50"
          )}
          onClick={() => setActiveField("name")}
        >
          <div className="text-[11px] font-semibold tracking-wider uppercase text-[var(--muted-foreground)]">Name</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => setActiveField("name")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                commitName(name)
              }
            }}
            onBlur={() => {
              // Commit on blur if the value actually changed vs the URL,
              // so tabbing or clicking away applies the search.
              if (name !== (searchParams.get("search") || "")) commitName(name)
            }}
            placeholder="Any residency"
            className="w-full text-[13px] mt-0.5 bg-transparent placeholder:text-[var(--muted-foreground)] text-[var(--foreground)] focus:outline-none truncate"
          />
        </div>

        <div className="w-px h-7 bg-[var(--border)] flex-shrink-0" />

        {/* What */}
        <button
          onClick={() => setActiveField(activeField === "what" ? null : "what")}
          className={cn(
            "flex-1 min-w-0 px-4 py-2 text-left transition-colors cursor-pointer",
            activeField === "what" ? "bg-[var(--secondary)]" : "hover:bg-[var(--secondary)]/50"
          )}
        >
          <div className="text-[11px] font-semibold tracking-wider uppercase text-[var(--muted-foreground)]">{t("search.what")}</div>
          <div className={cn("text-[13px] truncate mt-0.5", discipline ? "text-[var(--foreground)]" : "text-[var(--muted-foreground)]")}>
            {discipline || t("search.anyMedium")}
          </div>
        </button>

        <div className="w-px h-7 bg-[var(--border)] flex-shrink-0" />

        {/* Where */}
        <button
          onClick={() => setActiveField(activeField === "where" ? null : "where")}
          className={cn(
            "flex-1 min-w-0 px-4 py-2 text-left transition-colors cursor-pointer",
            activeField === "where" ? "bg-[var(--secondary)]" : "hover:bg-[var(--secondary)]/50"
          )}
        >
          <div className="text-[11px] font-semibold tracking-wider uppercase text-[var(--muted-foreground)]">{t("search.where")}</div>
          <div className={cn("text-[13px] truncate mt-0.5", continent ? "text-[var(--foreground)]" : "text-[var(--muted-foreground)]")}>
            {continent || t("search.anywhere")}
          </div>
        </button>

        <div className="w-px h-7 bg-[var(--border)] flex-shrink-0" />

        {/* Funding */}
        <button
          onClick={() => setActiveField(activeField === "funding" ? null : "funding")}
          className={cn(
            "flex-1 min-w-0 px-4 py-2 text-left transition-colors cursor-pointer",
            activeField === "funding" ? "bg-[var(--secondary)]" : "hover:bg-[var(--secondary)]/50"
          )}
        >
          <div className="text-[11px] font-semibold tracking-wider uppercase text-[var(--muted-foreground)]">{t("search.funding")}</div>
          <div className={cn("text-[13px] truncate mt-0.5", costType ? "text-[var(--foreground)]" : "text-[var(--muted-foreground)]")}>
            {costLabel || t("search.anyFunding")}
          </div>
        </button>

        {/* Search / Clear actions */}
        <div className="flex items-center gap-1 pr-1.5 flex-shrink-0">
          {hasFilters && (
            <button
              onClick={handleClear}
              className="p-1.5 rounded-md hover:bg-[var(--secondary)] transition-colors cursor-pointer"
              title="Clear filters"
            >
              <X size={13} className="text-[var(--muted-foreground)]" />
            </button>
          )}
          <button
            onClick={handleSearch}
            className="bg-[var(--primary)] text-white rounded-md p-2 hover:bg-[var(--primary)]/90 transition-colors cursor-pointer"
            aria-label="Search"
          >
            <Search size={14} />
          </button>
        </div>
      </div>

      {/* Mobile: compact pill */}
      <div className="md:hidden">
        <button
          onClick={() => setActiveField(activeField ? null : "name")}
          className="w-full flex items-center gap-3 border border-[var(--border)] rounded-lg px-4 py-2.5 bg-white"
        >
          <Search size={14} className="text-[var(--muted-foreground)] flex-shrink-0" />
          <div className="flex-1 text-left">
            <div className="text-[13px] text-[var(--foreground)]">
              {hasFilters
                ? [name, discipline, continent, costLabel].filter(Boolean).join(" / ")
                : t(mode === "directory" ? "search.searchDirectory" : "search.searchOpenCalls")
              }
            </div>
          </div>
          {hasFilters && (
            <button
              onClick={(e) => { e.stopPropagation(); handleClear() }}
              className="p-1 rounded-md hover:bg-[var(--secondary)]"
            >
              <X size={13} className="text-[var(--muted-foreground)]" />
            </button>
          )}
        </button>
      </div>

      {/* Dropdowns */}
      {activeField && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-lg border border-[var(--border)] shadow-lg z-50 overflow-hidden max-h-[60vh] overflow-y-auto">
          {/* Mobile field tabs */}
          <div className="md:hidden flex border-b border-[var(--border)]">
            {(["name", "what", "where", "funding"] as const).map((field) => (
              <button
                key={field}
                onClick={() => setActiveField(field)}
                className={cn(
                  "flex-1 py-2.5 text-[11px] font-semibold tracking-wider uppercase transition-colors",
                  activeField === field
                    ? "text-[var(--foreground)] border-b-2 border-[var(--primary)]"
                    : "text-[var(--muted-foreground)]"
                )}
              >
                {field === "name" ? "Name" : field === "what" ? t("search.what") : field === "where" ? t("search.where") : t("search.funding")}
              </button>
            ))}
          </div>

          {activeField === "name" && (
            <div className="p-4">
              <form onSubmit={(e) => { e.preventDefault(); commitName(name) }}>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Yaddo, MacDowell, Casa Gracia…"
                    autoFocus
                    className="w-full pl-9 pr-3 py-2 text-[13px] border border-[var(--border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary)] bg-white"
                  />
                </div>
                <p className="mt-2 text-[11px] text-[var(--muted-foreground)]">
                  Search by program name. Press Enter to apply.
                </p>
              </form>
            </div>
          )}

          {activeField === "what" && (
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {DISCIPLINES.map((d) => (
                  <button
                    key={d}
                    onClick={() => selectDiscipline(d)}
                    className={cn(
                      "px-3 py-1.5 text-[13px] rounded-md border transition-colors cursor-pointer",
                      discipline === d
                        ? "bg-[var(--foreground)] text-white border-[var(--foreground)]"
                        : "border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]"
                    )}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeField === "where" && (
            <div className="p-4 space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[var(--muted-foreground)] mb-2 font-semibold">By continent</p>
                <div className="flex flex-wrap gap-2">
                  {CONTINENTS.map((c) => (
                    <button
                      key={c}
                      onClick={() => selectContinent(c)}
                      className={cn(
                        "px-3 py-1.5 text-[13px] rounded-md border transition-colors cursor-pointer",
                        continent === c
                          ? "bg-[var(--foreground)] text-white border-[var(--foreground)]"
                          : "border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]"
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[var(--muted-foreground)] mb-2 font-semibold">By US state</p>
                <div className="flex flex-wrap gap-2">
                  {STATE_LINKS.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => selectStateLink(s.href)}
                      className="px-3 py-1.5 text-[13px] rounded-md border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)] transition-colors cursor-pointer"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-[var(--muted-foreground)]">
                  Opens the curated state collection.
                </p>
              </div>
            </div>
          )}

          {activeField === "funding" && (
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {COST_TYPES.map((ct) => (
                  <button
                    key={ct.value}
                    onClick={() => selectCostType(ct.value)}
                    className={cn(
                      "px-3 py-1.5 text-[13px] rounded-md border transition-colors cursor-pointer",
                      costType === ct.value
                        ? "bg-[var(--foreground)] text-white border-[var(--foreground)]"
                        : "border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]"
                    )}
                  >
                    {ct.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
